/**
 * <g-multiselect> - Multi-select with badge chips + filterable dropdown
 *
 * Declarative usage via native <option> children (consumed on first connect):
 *
 *   <g-multiselect name="tags" placeholder="Pick tags…">
 *     <option value="urgent" selected>Urgent</option>
 *     <option value="bug">Bug</option>
 *     <option value="feature" selected>Feature</option>
 *     <option value="backlog" disabled>Backlog</option>
 *   </g-multiselect>
 *
 * JS API:
 *   .options Array<{value, label, disabled?}>  full option list
 *   .value   Array<string>                     currently-selected values
 *
 * Attributes:
 *   name         used as the name on internal <input type="hidden"> per selected value
 *   placeholder  text shown in the inline filter input
 *   disabled     disables all interaction
 *
 * Events:
 *   change   detail: { value, added, removed }   bubbles, composed
 */

// Hide raw <option> children until the component consumes them. Without
// this they flash as plain text while the HTML parser is still appending
// children (custom-element upgrade runs before children are parsed).
const STYLE_ID = 'g-multiselect-styles';
if (typeof document !== 'undefined' && !document.getElementById(STYLE_ID)) {
  const s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent = 'g-multiselect > option { display: none !important; }';
  (document.head || document.documentElement).appendChild(s);
}

export class GMultiselect extends HTMLElement {
  constructor() {
    super();
    this._options = [];
    this._selected = [];
    this._filterText = '';
    this._open = false;
    this._activeIdx = -1;
    this._renderScheduled = false;
    this._initialized = false;
  }

  static get observedAttributes() {
    return ['name', 'placeholder', 'disabled'];
  }

  attributeChangedCallback() {
    if (!this.isConnected) return;
    this.scheduleRender();
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;
    this._buildShell();
    this._attachListeners();
    this._parseWhenReady();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._handleDocClick, true);
    this._childObserver?.disconnect();
  }

  _parseWhenReady() {
    // Try synchronously — covers the case where the element is upgraded
    // after its children were parsed (define called after parse).
    if (this.querySelector(':scope > option')) {
      this._parseOptionChildren();
      this.scheduleRender();
      return;
    }
    // Otherwise wait for the parser to add option children, then parse
    // on the next frame so all options arrive together.
    this._childObserver = new MutationObserver(() => {
      if (this.querySelector(':scope > option')) {
        this._childObserver.disconnect();
        requestAnimationFrame(() => {
          this._parseOptionChildren();
          this.scheduleRender();
        });
      }
    });
    this._childObserver.observe(this, { childList: true });
    this.scheduleRender();
  }

  // ── Public API ──

  get options() { return this._options; }
  set options(list) {
    this._options = normalizeOptions(list);
    // Drop any selected values that no longer exist
    const valid = new Set(this._options.map(o => o.value));
    this._selected = this._selected.filter(v => valid.has(v));
    this.scheduleRender();
  }

  get value() { return [...this._selected]; }
  set value(list) {
    const next = Array.isArray(list) ? list.map(String) : [];
    const valid = new Set(this._options.map(o => o.value));
    const filtered = dedupe(next.filter(v => valid.has(v)));
    const { added, removed } = diff(this._selected, filtered);
    if (!added.length && !removed.length) return;
    this._selected = filtered;
    this.scheduleRender();
    this._dispatchChange(added, removed);
  }

  // ── Internals ──

  _parseOptionChildren() {
    const opts = [];
    const selected = [];
    Array.from(this.querySelectorAll(':scope > option')).forEach(el => {
      const value = el.hasAttribute('value') ? el.getAttribute('value') : el.textContent.trim();
      const label = el.textContent.trim() || value;
      const disabled = el.hasAttribute('disabled');
      opts.push({ value, label, disabled });
      if (el.hasAttribute('selected')) selected.push(value);
    });
    this._options = normalizeOptions(opts);
    this._selected = dedupe(selected.filter(v => this._options.some(o => o.value === v && !o.disabled)));
    // Consume the option children
    Array.from(this.querySelectorAll(':scope > option')).forEach(el => el.remove());
  }

  _buildShell() {
    this._root = document.createElement('div');
    this._root.className = 'relative w-full';
    this._root.setAttribute('data-gms-root', '');

    this._control = document.createElement('div');
    this._control.className = 'input input-bordered min-h-10 h-auto py-1 flex flex-wrap items-center gap-1 cursor-text';
    this._control.setAttribute('tabindex', '0');
    this._control.setAttribute('role', 'combobox');
    this._control.setAttribute('aria-haspopup', 'listbox');
    this._control.setAttribute('aria-expanded', 'false');

    this._chips = document.createElement('span');
    this._chips.className = 'contents';
    this._chips.setAttribute('data-gms-chips', '');

    this._filter = document.createElement('input');
    this._filter.type = 'text';
    this._filter.autocomplete = 'off';
    this._filter.className = 'flex-1 min-w-[6ch] bg-transparent outline-none border-0 p-0 h-6';
    this._filter.setAttribute('data-gms-filter', '');

    this._control.appendChild(this._chips);
    this._control.appendChild(this._filter);

    this._list = document.createElement('ul');
    this._list.className = 'menu bg-base-100 rounded-box shadow-lg border border-base-300 ' +
      'absolute left-0 right-0 mt-1 z-50 max-h-60 overflow-y-auto hidden p-1 flex-nowrap';
    this._list.setAttribute('role', 'listbox');
    this._list.setAttribute('data-gms-list', '');

    this._root.appendChild(this._control);
    this._root.appendChild(this._list);

    this._hiddenWrap = document.createElement('span');
    this._hiddenWrap.setAttribute('data-gms-hidden', '');

    this.appendChild(this._root);
    this.appendChild(this._hiddenWrap);
  }

  _attachListeners() {
    this._root.addEventListener('click', this._handleClick);
    this._control.addEventListener('mousedown', this._handleControlMousedown);
    this._filter.addEventListener('input', this._handleFilterInput);
    this._filter.addEventListener('keydown', this._handleFilterKeydown);
    this._filter.addEventListener('focus', this._handleFilterFocus);
    document.addEventListener('click', this._handleDocClick, true);
  }

  scheduleRender() {
    if (this._renderScheduled) return;
    this._renderScheduled = true;
    requestAnimationFrame(() => {
      this._renderScheduled = false;
      this._render();
    });
  }

  _render() {
    if (!this._root) return;
    const disabled = this.hasAttribute('disabled');
    this._root.classList.toggle('pointer-events-none', disabled);
    this._root.classList.toggle('opacity-60', disabled);
    this._filter.disabled = disabled;
    this._filter.placeholder = this._selected.length ? '' : (this.getAttribute('placeholder') || '');

    // Chips
    const labelOf = (v) => {
      const o = this._options.find(o => o.value === v);
      return o ? o.label : v;
    };
    this._chips.innerHTML = this._selected.map(v => `
      <span class="badge badge-primary gap-1" data-gms-chip data-value="${escapeHtml(v)}">
        ${escapeHtml(labelOf(v))}
        <button type="button" class="cursor-pointer opacity-70 hover:opacity-100 leading-none"
                aria-label="Remove ${escapeHtml(labelOf(v))}" data-gms-remove="${escapeHtml(v)}"
                tabindex="-1">&times;</button>
      </span>
    `).join('');

    // Dropdown list
    const filter = this._filterText.trim().toLowerCase();
    const visible = this._options.filter(o =>
      !o.disabled &&
      !this._selected.includes(o.value) &&
      (filter === '' || o.label.toLowerCase().includes(filter))
    );
    if (this._activeIdx >= visible.length) this._activeIdx = visible.length - 1;
    if (this._activeIdx < 0 && visible.length) this._activeIdx = 0;

    if (visible.length === 0) {
      this._list.innerHTML = `<li class="px-3 py-2 text-base-content/60 text-sm pointer-events-none">No matches</li>`;
    } else {
      this._list.innerHTML = visible.map((o, i) => {
        const active = i === this._activeIdx ? 'bg-base-200' : '';
        return `<li>
          <a data-gms-option data-value="${escapeHtml(o.value)}" role="option"
             aria-selected="false" tabindex="-1" class="${active}">${escapeHtml(o.label)}</a>
        </li>`;
      }).join('');
    }

    this._list.classList.toggle('hidden', !this._open);
    this._control.setAttribute('aria-expanded', String(this._open));

    // Scroll active into view
    if (this._open && this._activeIdx >= 0) {
      const activeEl = this._list.querySelector(`[data-gms-option]:nth-of-type(${this._activeIdx + 1})`);
      activeEl?.scrollIntoView({ block: 'nearest' });
    }

    // Hidden inputs
    const name = this.getAttribute('name');
    if (name) {
      this._hiddenWrap.innerHTML = this._selected.map(v =>
        `<input type="hidden" name="${escapeHtml(name)}" value="${escapeHtml(v)}">`
      ).join('');
    } else {
      this._hiddenWrap.innerHTML = '';
    }
  }

  _visibleOptions() {
    const filter = this._filterText.trim().toLowerCase();
    return this._options.filter(o =>
      !o.disabled &&
      !this._selected.includes(o.value) &&
      (filter === '' || o.label.toLowerCase().includes(filter))
    );
  }

  _addValue(v) {
    if (!v) return;
    const opt = this._options.find(o => o.value === v);
    if (!opt || opt.disabled) return;
    if (this._selected.includes(v)) return;
    this._selected = [...this._selected, v];
    this._filterText = '';
    this._filter.value = '';
    this._activeIdx = 0;
    this._open = false;
    this.scheduleRender();
    this._dispatchChange([v], []);
  }

  _removeValue(v) {
    if (!this._selected.includes(v)) return;
    this._selected = this._selected.filter(x => x !== v);
    this.scheduleRender();
    this._dispatchChange([], [v]);
  }

  _open_() {
    if (this._open || this.hasAttribute('disabled')) return;
    this._open = true;
    if (this._activeIdx < 0 && this._visibleOptions().length) this._activeIdx = 0;
    this.scheduleRender();
  }

  _close() {
    if (!this._open) return;
    this._open = false;
    this.scheduleRender();
  }

  _dispatchChange(added, removed) {
    this.dispatchEvent(new CustomEvent('change', {
      detail: { value: [...this._selected], added, removed },
      bubbles: true,
      composed: true,
    }));
  }

  // ── Event handlers ──

  _handleClick = (e) => {
    if (this.hasAttribute('disabled')) return;
    const remove = e.target.closest('[data-gms-remove]');
    if (remove && this._root.contains(remove)) {
      e.preventDefault();
      this._removeValue(remove.getAttribute('data-gms-remove'));
      this._filter.focus();
      return;
    }
    const opt = e.target.closest('[data-gms-option]');
    if (opt && this._list.contains(opt)) {
      e.preventDefault();
      this._addValue(opt.getAttribute('data-value'));
      return;
    }
  };

  _handleControlMousedown = (e) => {
    if (this.hasAttribute('disabled')) return;
    // Clicking on chip remove button or filter: let default fire
    if (e.target.closest('[data-gms-remove]')) return;
    if (e.target === this._filter) return;
    // Clicking elsewhere in the input shell: focus filter and open
    e.preventDefault();
    this._filter.focus();
    this._open_();
  };

  _handleFilterInput = () => {
    this._filterText = this._filter.value;
    this._activeIdx = 0;
    this._open_();
    this.scheduleRender();
  };

  _handleFilterFocus = () => {
    this._open_();
  };

  _handleFilterKeydown = (e) => {
    const visible = this._visibleOptions();
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!this._open) { this._open_(); return; }
      if (!visible.length) return;
      this._activeIdx = (this._activeIdx + 1) % visible.length;
      this.scheduleRender();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!this._open) { this._open_(); return; }
      if (!visible.length) return;
      this._activeIdx = (this._activeIdx - 1 + visible.length) % visible.length;
      this.scheduleRender();
    } else if (e.key === 'Enter') {
      if (this._open && this._activeIdx >= 0 && visible[this._activeIdx]) {
        e.preventDefault();
        this._addValue(visible[this._activeIdx].value);
      }
    } else if (e.key === 'Escape') {
      if (this._open) {
        e.preventDefault();
        this._close();
      }
    } else if (e.key === 'Backspace') {
      if (this._filter.value === '' && this._selected.length) {
        e.preventDefault();
        this._removeValue(this._selected[this._selected.length - 1]);
      }
    }
  };

  _handleDocClick = (e) => {
    if (!this._open) return;
    if (this.contains(e.target)) return;
    this._close();
  };
}

function normalizeOptions(list) {
  if (!Array.isArray(list)) return [];
  const seen = new Set();
  const out = [];
  for (const item of list) {
    if (!item) continue;
    const value = String(item.value ?? '').trim();
    if (!value || seen.has(value)) continue;
    seen.add(value);
    out.push({
      value,
      label: String(item.label ?? value),
      disabled: !!item.disabled,
    });
  }
  return out;
}

function dedupe(arr) {
  const seen = new Set();
  const out = [];
  for (const v of arr) {
    if (seen.has(v)) continue;
    seen.add(v);
    out.push(v);
  }
  return out;
}

function diff(prev, next) {
  const prevSet = new Set(prev);
  const nextSet = new Set(next);
  const added = next.filter(v => !prevSet.has(v));
  const removed = prev.filter(v => !nextSet.has(v));
  return { added, removed };
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}

if (!customElements.get('g-multiselect')) {
  customElements.define('g-multiselect', GMultiselect);
}
